import { api } from '@/services/backend/http-client';
import { DataTableSortConfig } from '@opaz/ui-components';
import { AxiosError, type AxiosRequestConfig } from 'axios';
import {
  FlowablePageRequestType,
  FlowablePageResponse,
  NextInvolvedTaskResponseType,
  ProcessInstanceResponseType,
  ProcessInstanceType,
  ProcessTaskListType,
  StartProcessRequestType,
  StartProcessResponseType,
  StartRegistryProcessType,
  TaskDetailsType,
  TaskHistoryRequest,
  TaskHistoryResponse,
  TasksResponseType,
  WorkDefinitionResponseType,
} from './types';

export const getTasks = async (
  pageRequest: FlowablePageRequestType,
  filter: {
    state?: string;
    searchTerm?: string;
  } | null,
): Promise<FlowablePageResponse<TasksResponseType>> => {
  const response = await api.get<FlowablePageResponse<TasksResponseType>>(`/workflow/search/tasks`, {
    params: {
      filterId: 'myTasks',
      ...pageRequest,
      ...filter,
    },
  });
  return response.data;
};

export const getProcessInstances = async (
  pageRequest: FlowablePageRequestType,
  sortConfig: DataTableSortConfig | null,
  filter: {
    searchTerm?: string;
    status?: string;
    active?: boolean;
  } | null,
): Promise<FlowablePageResponse<ProcessInstanceResponseType>> => {
  const response = await api.get<FlowablePageResponse<ProcessInstanceResponseType>>(
    '/workflow/search/process-instances',
    {
      params: {
        filterId: 'myProcesses',
        ...pageRequest,
        ...filter,
        ...(sortConfig && { sort: sortConfig.key, order: sortConfig.direction }),
      },
    },
  );
  return response.data;
};

export const getProcesses = async (): Promise<WorkDefinitionResponseType[]> => {
  const response = await api.get<WorkDefinitionResponseType[]>(`/workflow/work-definitions`);
  return response.data;
};

export const getProcessInstance = async (
  processInstanceId: string,
  navigatedFrom?: string,
  ignoreStatusCodes: number[] = [],
): Promise<ProcessInstanceType> => {
  const response = await api.get<ProcessInstanceType>(`/workflow/process-instances/${processInstanceId}`, {
    responseInterceptorConfig: {
      ignoreStatusCodes: [...(navigatedFrom === 'task' ? [404, 500] : []), ...ignoreStatusCodes],
    },
  });
  return response.data;
};

export const getProcessTasks = async (processInstanceId: string): Promise<ProcessTaskListType> => {
  const response = await api.get<ProcessTaskListType>(
    `/workflow/runtime/tasks?start=0&size=20&processInstanceIdWithChildren=${processInstanceId}&includeTranslations=false&sort=createTime`,
  );
  return response.data;
};

export const getWorkForm = async (processInstanceId: string): Promise<{ [key: string]: unknown }> => {
  const response = await api.get<{ [key: string]: unknown }>(
    `/workflow/process-instances/${processInstanceId}/work-form`,
  );
  return response.data;
};

export const getWorkFormVariables = async (processInstanceId: string): Promise<{ [key: string]: unknown }> => {
  const response = await api.get<{ [key: string]: unknown }>(
    `/workflow/process-instances/${processInstanceId}/work-form/variables`,
  );
  return response.data;
};

export const getTaskDetails = async (taskId: string, config?: AxiosRequestConfig): Promise<TaskDetailsType> => {
  const response = await api.get<TaskDetailsType>(`/workflow/tasks/${taskId}`, config);
  return response.data;
};

export const getTaskVariables = async (
  taskId: string,
  config?: AxiosRequestConfig,
): Promise<{ [key: string]: unknown }> => {
  const response = await api.get<{ [key: string]: unknown }>(`/workflow/tasks/${taskId}/variables`, config);
  return response.data;
};

export const getTaskForm = async (taskId: string, config?: AxiosRequestConfig): Promise<{ [key: string]: unknown }> => {
  const response = await api.get<{ [key: string]: unknown }>(`/workflow/tasks/${taskId}/form`, config);
  return response.data;
};

export const getNextInvolvedTask = async (processInstanceId: string): Promise<NextInvolvedTaskResponseType> => {
  const response = await api.get<NextInvolvedTaskResponseType>(
    `/workflow/next-involved-task/bpmn/${processInstanceId}`,
  );
  return response.data;
};

export const getNextInvolvedTaskWithFallback = async (
  processInstanceId: string,
): Promise<NextInvolvedTaskResponseType | null> => {
  try {
    const response = await api.get<NextInvolvedTaskResponseType>(
      `/workflow/next-involved-task/bpmn/${processInstanceId}`,
    );
    return response.data;
  } catch (error: unknown) {
    if ((error as AxiosError)?.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const navigateToProcessOrTask = async (
  processInstanceId: string,
  navigate: (options: {
    to: string;
    params?: Record<string, string>;
    search?: Record<string, string | undefined>;
  }) => void,
) => {
  const nextTask = await getNextInvolvedTaskWithFallback(processInstanceId);

  if (nextTask) {
    navigate({
      to: '/processes/$processInstanceId/task/$taskId',
      params: {
        processInstanceId,
        taskId: nextTask.id,
      },
      search: {
        responseToken: undefined,
      },
    });
  } else {
    navigate({
      to: '/processes/$processInstanceId',
      params: {
        processInstanceId,
      },
      search: {
        from: undefined,
      },
    });
  }
};

export const startProcess = async (
  { processDefinitionId, serviceCode, serviceDefinitionCode, zoneCode, submissionChannel }: StartProcessRequestType,
  config?: AxiosRequestConfig,
): Promise<StartProcessResponseType> => {
  const response = await api.post<StartProcessResponseType>(
    `/workflow/process-instances`,
    {
      processDefinitionId,
      serviceCode,
      serviceDefinitionCode,
      zoneCode,
      submissionChannel,
    },
    config,
  );
  return response.data;
};

export const startRegistryProcess = async ({
  type,
  serviceCode,
  zoneCode,
  relatedRegistryId,
}: StartRegistryProcessType): Promise<StartProcessResponseType> => {
  const response = await api.post<StartProcessResponseType>(`/workflow/process-instances`, {
    type,
    serviceCode,
    zoneCode,
    relatedRegistryId,
  });
  return response.data;
};

export const saveTaskForm = async (
  taskId: string,
  formData: { [key: string]: unknown },
  config?: AxiosRequestConfig,
): Promise<void> => {
  const response = await api.post<void>(`/workflow/tasks/${taskId}/save-form`, formData, config);
  return response.data;
};

export const completeTaskForm = async (
  taskId: string,
  formData: { [key: string]: unknown },
  config?: AxiosRequestConfig,
): Promise<void> => {
  const response = await api.post<void>(`/workflow/tasks/${taskId}/complete`, formData, config);
  return response.data;
};

export const getHistoricTaskInstances = async (
  processInstanceId: string,
  options: Partial<Omit<TaskHistoryRequest, 'processInstanceIdWithChildren'>> = {},
): Promise<TaskHistoryResponse> => {
  const response = await api.get<TaskHistoryResponse>(`/workflow/history/historic-task-instances`, {
    params: {
      processInstanceIdWithChildren: processInstanceId,
      start: options.start ?? 0,
      size: options.size ?? 20,
      includeTranslations: options.includeTranslations ?? true,
      sort: options.sort ?? 'start',
      order: options.order ?? 'desc',
    },
  });
  return response.data;
};
